/* PUMPLAB_REVIEW_WRITE_V1: <pl-review-write> verified-buyer review pop-up.
   States: checking, login, not-buyer, already, form, sending, done, error.
   Server: /apps/loyalty/reviews/eligibility (GET) + /reviews/submit (POST) via the Shopify App Proxy.
   Images: resized client-side to 1600px webp, uploaded to Judge.me's presigned S3, keys sent to submit. */
if (!customElements.get('pl-review-write')) {
  const MAX_IMAGES = 3;
  const MAX_EDGE = 1600;
  const MAX_BLOB = 4 * 1024 * 1024;
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([hidden]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]';
  const FORM_STATES = new Set(['form', 'sending', 'error']);

  customElements.define('pl-review-write', class extends HTMLElement {
    connectedCallback() {
      // any-product mode (account page tab panels): lift the pop-up to <body> so no hidden/transformed ancestor can clip it
      if (!this.dataset.productId && this.parentNode !== document.body) { document.body.appendChild(this); return; }
      if (this._ready) return;
      this._ready = true;
      this.state = 'checking';
      this.images = []; // {blob, ext, url, key}
      this.rating = 0;
      this.rec = null;
      this.productId = this.dataset.productId;
      // empty data-product-id = "any product" mode (account page): the opening event supplies the product
      this.anyProduct = !this.dataset.productId;
      this.preVariant = null;
      try { this.labels = JSON.parse(this.querySelector('[data-rw-labels]').textContent); } catch (e) { this.labels = {}; }
      this.form = this.querySelector('form');
      this.panel = this.querySelector('.pl-rw__panel');
      this.signal = (this.ac = new AbortController()).signal;
      document.addEventListener('pl:write-review', (e) => this.onOpenEvent(e), { signal: this.signal });
      window.addEventListener('hashchange', () => { if (location.hash === '#write-review') this.open(); }, { signal: this.signal });
      this.form.elements.variantId.addEventListener('change', () => this.updateFlavourLine(), { signal: this.signal });
      const stars = this.querySelector('[data-rw-stars]');
      stars.addEventListener('mouseover', (e) => { const b = e.target.closest('[data-rw-star]'); if (b) { this.showWord(Number(b.dataset.rwStar)); this.paintStars(Number(b.dataset.rwStar)); } }, { signal: this.signal });
      stars.addEventListener('mouseleave', () => { this.showWord(this.rating); this.paintStars(this.rating); }, { signal: this.signal });
      stars.addEventListener('focusin', (e) => { const b = e.target.closest('[data-rw-star]'); if (b) { this.showWord(Number(b.dataset.rwStar)); this.paintStars(Number(b.dataset.rwStar)); } }, { signal: this.signal });
      stars.addEventListener('focusout', () => { this.showWord(this.rating); this.paintStars(this.rating); }, { signal: this.signal });
      this.addEventListener('click', (e) => this.onClick(e), { signal: this.signal });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); }, { signal: this.signal });
      this.form.addEventListener('input', () => this.hideError(), { signal: this.signal });
      this.querySelector('[data-rw-file]').addEventListener('change', (e) => this.addFiles(e.target), { signal: this.signal });
      document.addEventListener('keydown', (e) => this.onKey(e), { signal: this.signal });
      // Desktop drag and drop: drop images anywhere on the form
      const dz = this.form, hasFiles = (e) => e.dataTransfer && [...e.dataTransfer.types].includes('Files');
      let depth = 0;
      dz.addEventListener('dragenter', (e) => { if (!hasFiles(e)) return; e.preventDefault(); depth++; this.classList.add('is-drag'); }, { signal: this.signal });
      dz.addEventListener('dragover', (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }, { signal: this.signal });
      dz.addEventListener('dragleave', () => { if (--depth <= 0) { depth = 0; this.classList.remove('is-drag'); } }, { signal: this.signal });
      dz.addEventListener('drop', (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault(); depth = 0; this.classList.remove('is-drag');
        if (this.state === 'sending') return;
        const files = [...e.dataTransfer.files].filter((f) => /^image\//.test(f.type));
        if (files.length) this.addFiles({ files, value: '' });
      }, { signal: this.signal });
      this.querySelector('[data-rw-add]').addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.querySelector('[data-rw-file]').click(); } }, { signal: this.signal });
      this.initRoving('[data-rw-stars]', '[data-rw-star]', (b) => this.setRating(Number(b.dataset.rwStar)));
      this.initRoving('[data-rw-rec]', '[data-rw-rec-val]', (b) => this.setRec(b.dataset.rwRecVal));
      if (location.hash === '#write-review') this.open();
    }
    disconnectedCallback() { this.ac && this.ac.abort(); this._ready = false; this.unlock(); }

    initRoving(groupSel, itemSel, select) {
      const group = this.querySelector(groupSel);
      const items = () => [...group.querySelectorAll(itemSel)];
      items().forEach((b, i) => b.setAttribute('tabindex', i === 0 ? '0' : '-1'));
      group.addEventListener('keydown', (e) => {
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
        if (!(e.key in keys)) return;
        e.preventDefault();
        const list = items();
        const i = list.indexOf(document.activeElement);
        const next = list[(i + keys[e.key] + list.length) % list.length];
        next.focus();
        select(next);
      }, { signal: this.signal });
    }

    rove(groupSel, itemSel, checkedEl) {
      const items = [...this.querySelectorAll(groupSel + ' ' + itemSel)];
      items.forEach((b, i) => b.setAttribute('tabindex', (checkedEl ? b === checkedEl : i === 0) ? '0' : '-1'));
    }

    onKey(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') { if (this.state !== 'sending') this.close(); return; }
      if (e.key !== 'Tab') return;
      const list = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => !el.closest('[hidden]') && el.getAttribute('tabindex') !== '-1');
      if (!list.length) { e.preventDefault(); this.panel.focus(); return; }
      const first = list[0], last = list[list.length - 1], a = document.activeElement;
      if (!this.panel.contains(a)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && (a === first || a === this.panel)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
    }

    onOpenEvent(e) {
      const d = e.detail || {};
      const pid = d.productId;
      if (this.anyProduct) {
        if (!pid || this.isOpen) return;
        this.setProduct(d);
      } else if (pid && String(pid) !== String(this.productId)) return;
      this.preVariant = d.variantId ? String(d.variantId) : null;
      this.returnFocus = (e.detail && e.detail.trigger) || document.activeElement;
      this.open();
    }

    /* account mode: switch the pop-up to the product named by the event; a different product starts a clean form */
    setProduct(d) {
      const id = String(d.productId);
      if (id !== String(this.productId)) this.resetForm();
      this.productId = id;
      this.dataset.productTitle = d.title || '';
      const box = this.querySelector('.pl-rw__product');
      if (!box) return;
      let img = box.querySelector('img');
      if (d.image) {
        if (!img) { img = document.createElement('img'); img.alt = ''; img.width = 64; img.height = 64; box.prepend(img); }
        img.src = d.image;
      } else if (img) img.remove();
      const strong = box.querySelector('strong'), span = box.querySelector('span');
      if (strong) {
        strong.textContent = d.title || '';
        // Same trademark exclusions as the account order cards
        strong.classList.toggle('has-tm', !/gift card|bundle|sample|shaker/i.test(d.title || ''));
      }
      if (span) span.textContent = '';
    }

    resetForm() {
      this.form.reset();
      this.images.forEach((im) => URL.revokeObjectURL(im.url));
      this.images = [];
      this.renderImages();
      this.setRating(0);
      this.setRec(null);
      this.hideError();
    }

    get isOpen() { return !this.hidden; }

    setState(state) {
      this.state = state;
      this.dataset.state = state;
      const shown = FORM_STATES.has(state) ? 'form' : state;
      this.querySelectorAll('[data-rw-state]').forEach((s) => { s.hidden = s.dataset.rwState !== shown; });
      const busy = state === 'sending';
      const btn = this.querySelector('[data-rw-submit]');
      btn.disabled = busy;
      btn.querySelector('[data-rw-submit-label]').textContent = busy ? this.labels.write_sending : this.labels.write_submit;
      btn.querySelector('[data-rw-reward-wrap]').hidden = busy;
      this.form.toggleAttribute('aria-busy', busy);
      const section = this.querySelector('[data-rw-state="' + shown + '"]');
      const h = section && section.querySelector('h2[id]');
      if (h) { this.panel.setAttribute('aria-labelledby', h.id); this.panel.removeAttribute('aria-label'); }
      else { this.panel.removeAttribute('aria-labelledby'); this.panel.setAttribute('aria-label', this.dataset.productTitle || ''); }
      const act = document.activeElement;
      if (this.isOpen && (!act || !this.panel.contains(act) || act.closest('[hidden]'))) this.panel.focus({ preventScroll: true });
    }

    async open() {
      if (this.isOpen || !this.productId) return;
      this.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      this.lock();
      this.setState('checking');
      this.panel.focus({ preventScroll: true });
      await this.check();
    }

    async check() {
      this.setState('checking');
      const here = location.pathname + location.search;
      const ret = encodeURIComponent(here + '#write-review');
      this.querySelector('[data-rw-login]').href = this.dataset.loginUrl + '?return_url=' + ret;
      this.querySelector('[data-rw-register]').href = this.dataset.registerUrl + '?return_url=' + ret;
      let elig;
      try {
        const res = await fetch('/apps/loyalty/reviews/eligibility?product=' + encodeURIComponent(this.productId), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        elig = await res.json();
      } catch (e) { elig = null; }
      if (!this.isOpen) return;
      if (!elig || elig.ok === false) { this.setState('load-error'); this.querySelector('[data-rw-retry]').focus({ preventScroll: true }); return; }
      if (!elig.loggedIn) return this.setState('login');
      if (elig.already) return this.setState('already');
      if (!elig.bought || !(elig.variants || []).length) return this.setState('not-buyer');
      this.fillVariants(elig.variants);
      this.setState('form');
      this.panel.focus({ preventScroll: true }); // panel, not star 1: focusing a star showed a ring and a 1-star preview
    }

    close() {
      if (this.state === 'sending') return;
      this.hidden = true;
      this.classList.remove('is-open');
      this.unlock();
      if (location.hash === '#write-review') history.replaceState(null, '', location.pathname + location.search);
      const f = this.returnFocus; this.returnFocus = null;
      if (f && f.focus) f.focus({ preventScroll: true }); else if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    }

    lock() { document.documentElement.classList.add('pl-rw-lock'); }
    unlock() { document.documentElement.classList.remove('pl-rw-lock'); }

    fillVariants(variants) {
      const wrap = this.querySelector('[data-rw-flavour]');
      const sel = this.form.elements.variantId;
      const prev = sel.value;
      sel.innerHTML = '';
      variants.forEach((v) => { const o = document.createElement('option'); o.value = v.id; o.textContent = v.title; sel.appendChild(o); });
      const want = this.preVariant || prev;
      if (want && variants.some((v) => String(v.id) === String(want))) sel.value = want;
      wrap.hidden = variants.length < 2;
      this.updateFlavourLine();
    }

    updateFlavourLine() {
      const sel = this.form.elements.variantId;
      const line = this.querySelector('[data-rw-flavour-line]');
      const o = sel && sel.selectedOptions && sel.selectedOptions[0];
      if (line) line.textContent = o ? o.textContent : '';
      // Show the chosen flavour's own image (PDP mode; account mode passes the line's variant image)
      if (!o) return;
      let map = {};
      try { map = JSON.parse((this.querySelector('[data-rw-vimgs]') || {}).textContent || '{}'); } catch (e) { map = {}; }
      const src = map[o.value];
      const img = this.querySelector('.pl-rw__product img');
      if (src && img && !this.anyProduct) img.src = src;
    }

    paintStars(n) {
      this.querySelectorAll('[data-rw-star]').forEach((b) => b.classList.toggle('is-on', Number(b.dataset.rwStar) <= n));
    }

    updateReward() {
      const btn = this.querySelector('[data-rw-submit]');
      const el = btn.querySelector('[data-rw-reward]');
      const next = this.images.length ? btn.dataset.image : btn.dataset.base;
      if (el.textContent === next) return;
      el.textContent = next;
      el.classList.remove('is-pop'); void el.offsetWidth; el.classList.add('is-pop');
    }

    showWord(n) {
      const out = this.querySelector('[data-rw-word-out]');
      const b = n ? this.querySelector('[data-rw-star="' + n + '"]') : null;
      out.textContent = b ? b.dataset.rwWord : '';
    }

    onClick(e) {
      if (e.target.closest('[data-rw-retry]')) { this.check(); return; }
      if (e.target.closest('[data-rw-close]')) { this.close(); return; }
      const star = e.target.closest('[data-rw-star]');
      if (star) { this.setRating(Number(star.dataset.rwStar)); return; }
      const rec = e.target.closest('[data-rw-rec-val]');
      if (rec) { this.setRec(rec.dataset.rwRecVal); return; }
      const rm = e.target.closest('[data-rw-remove]');
      if (rm) { this.removeImage(Number(rm.dataset.rwRemove)); }
    }

    setRating(n) {
      this.rating = n;
      this.showWord(n);
      this.querySelectorAll('[data-rw-star]').forEach((b) => {
        const v = Number(b.dataset.rwStar);
        b.setAttribute('aria-checked', String(v === n));
        b.classList.toggle('is-on', v <= n);
      });
      this.rove('[data-rw-stars]', '[data-rw-star]', this.querySelector('[data-rw-star="' + n + '"]'));
      this.hideError();
    }
    setRec(v) {
      this.rec = v;
      this.querySelectorAll('[data-rw-rec-val]').forEach((b) => b.setAttribute('aria-checked', String(b.dataset.rwRecVal === v)));
      this.rove('[data-rw-rec]', '[data-rw-rec-val]', this.querySelector('[data-rw-rec-val="' + v + '"]'));
      this.hideError();
    }

    showError(msg) { const p = this.querySelector('[data-rw-error]'); p.textContent = msg || this.labels.write_err_generic; p.hidden = false; }
    hideError() { const p = this.querySelector('[data-rw-error]'); if (p) p.hidden = true; }

    /* ---- images ---- */
    async addFiles(input) {
      const files = [...input.files];
      input.value = '';
      for (const file of files) {
        if (this.images.length >= MAX_IMAGES) break;
        try {
          const img = await this.prepare(file);
          this.images.push(img);
        } catch (e) { this.showError(this.labels.write_err_image); }
      }
      this.renderImages();
    }

    async prepare(file) {
      const bmp = await this.decode(file);
      const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
      const w = Math.max(1, Math.round(bmp.width * scale));
      const h = Math.max(1, Math.round(bmp.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(bmp, 0, 0, w, h);
      if (bmp.close) bmp.close();
      let blob = await new Promise((r) => canvas.toBlob(r, 'image/webp', 0.85));
      let ext = 'webp';
      if (!blob || blob.type !== 'image/webp') { blob = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.85)); ext = 'jpg'; }
      if (!blob || blob.size > MAX_BLOB) throw new Error('image');
      return { blob, ext, url: URL.createObjectURL(blob), key: null };
    }

    async decode(file) {
      if (window.createImageBitmap) { try { return await createImageBitmap(file); } catch (e) { /* fall through */ } }
      const url = URL.createObjectURL(file);
      try {
        return await new Promise((resolve, reject) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = url;
        });
      } finally { setTimeout(() => URL.revokeObjectURL(url), 5000); }
    }

    removeImage(i) {
      const [gone] = this.images.splice(i, 1);
      if (gone) URL.revokeObjectURL(gone.url);
      this.renderImages();
    }

    renderImages() {
      const wrap = this.querySelector('[data-rw-images]');
      wrap.querySelectorAll('.pl-rw__thumb').forEach((n) => n.remove());
      const add = wrap.querySelector('[data-rw-add]');
      this.images.forEach((im, i) => {
        const d = document.createElement('div');
        d.className = 'pl-rw__thumb';
        const img = document.createElement('img'); img.src = im.url; img.alt = '';
        const b = document.createElement('button'); b.type = 'button'; b.dataset.rwRemove = String(i); b.setAttribute('aria-label', this.labels.write_remove_image);
        b.innerHTML = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/></svg>';
        d.append(img, b);
        wrap.insertBefore(d, add);
      });
      add.hidden = this.images.length >= MAX_IMAGES;
      this.updateReward();
    }

    async uploadImage(im) {
      if (im.key) return im.key;
      const q = new URLSearchParams({ shop_domain: this.dataset.shop, platform: 'shopify', api_token: this.dataset.token });
      const pre = await fetch('https://api.judge.me/api/v1/pictures/presigned_data?' + q);
      if (!pre.ok) throw new Error('presign');
      const data = await pre.json();
      const name = 'review-' + Math.random().toString(36).slice(2, 10) + '.' + im.ext;
      const key = data.key_prefix + name;
      const fd = new FormData();
      fd.append('key', key);
      Object.entries(data.fields || {}).forEach(([k, v]) => fd.append(k, v));
      fd.append('file', im.blob, name);
      const up = await fetch(data.url, { method: 'POST', body: fd });
      if (up.status !== 201 && !up.ok) throw new Error('s3');
      im.key = key;
      return key;
    }

    /* ---- submit ---- */
    async submit() {
      if (this.state === 'sending') return;
      this.hideError();
      const body = this.form.elements.body.value.trim();
      if (!this.rating || !this.rec) return this.showError(this.labels.write_err_required);
      if (body.length < 5) return this.showError(this.labels.write_err_short);
      this.setState('sending');
      let keys;
      try { keys = []; for (const im of this.images) keys.push(await this.uploadImage(im)); }
      catch (e) { this.setState('error'); return this.showError(this.labels.write_err_upload); }
      let res;
      try {
        const r = await fetch('/apps/loyalty/reviews/submit', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            productId: this.productId,
            variantId: this.form.elements.variantId.value,
            rating: this.rating,
            recommend: this.rec === 'yes',
            title: this.form.elements.title.value.trim(),
            body,
            imageKeys: keys,
          }),
        });
        res = await r.json();
      } catch (e) { this.setState('error'); return this.showError(this.labels.write_err_network); }
      if (res && res.ok) {
        const dp = this.querySelector('[data-rw-done-pts]');
        if (dp) dp.textContent = this.images.length ? dp.dataset.image : dp.dataset.base;
        this.setState('done');
        document.dispatchEvent(new CustomEvent('pl:review-submitted', { detail: { productId: String(this.productId) } }));
        return;
      }
      const map = { already: 'write_err_already', not_eligible: 'write_err_not_eligible', rate_limited: 'write_err_rate', not_logged_in: 'write_err_not_eligible' };
      this.setState('error');
      this.showError(this.labels[map[res && res.error]] || this.labels.write_err_generic);
    }
  });
}
